import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import {
    HelperText,
    TextInput as PaperTextInput,
    type TextInputProps as PaperTextInputProps,
    useTheme,
} from 'react-native-paper'
import type { AppTheme } from '@/types/theme'

export type TextInputProps<T extends FieldValues = Record<string, unknown>> = Omit<
    PaperTextInputProps,
    'name' | 'value' | 'onChangeText' | 'onBlur'
> & {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, unknown>
    label?: string
    hint?: string
    wrapperProps?: ViewProps
}

export function TextInput<T extends FieldValues = Record<string, unknown>>(
    props: TextInputProps<T>
) {
    const { label, hint, name, control, rules, wrapperProps = {}, ...inputProps } = props
    const theme = useTheme<AppTheme>()
    const {
        field,
        fieldState: { error, invalid: _invalid, isTouched: _isTouched, isDirty: _isDirty },
    } = useController({
        name,
        control,
        rules,
    })

    return (
        <View style={[styles.inputWrapper, wrapperProps.style]} {...wrapperProps}>
            <PaperTextInput
                {...inputProps}
                label={label}
                mode="outlined"
                value={field.value || ''}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                error={!!error}
                accessibilityLabel={label}
                testID={name}
                style={{ zIndex: 2 }}
                placeholderTextColor={theme.custom.placeholder}
            />
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
})
